import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDistricts,
  fetchMunicipalities,
  fetchWards,
  fetchCanteenTypes,
  fetchCanteenList,
} from "../../redux/actions/locationActions";

import {
  fetchSurveyList,
  fetchSurveyDetail,
  fetchUserSurveyList,
} from "../../redux/actions/surveyActions";

import {
  fetchKitchenSurveyList,
  fetchKitchenSurveyDetail,
  fetchKitchenUserSurveyList,
} from "../../redux/actions/kitchenSurveyActions";

import {
  fetchKitchenDiningSurveyList,
  fetchKitchenDiningSurveyDetail,
  fetchKitchenDiningUserSurveyList,
} from "../../redux/actions/kitchenDiningSurveyActions";

import { MapPin, Home } from "lucide-react";

// Import all components
import CanteenCard from "./CanteenCard";
import KioskSurveyList from "./KioskSurveyList";
import KioskSurveyDetail from "./KioskSurveyDetail";
import KitchenSurveyList from "./KitchenSurveyList";
import KitchenSurveyDetail from "./KitchenSurveyDetail";
import KitchenDiningSurveyList from "./KitchenDiningSurveyList";
import KitchenDiningSurveyDetail from "./KitchenDiningSurveyDetail";
import { useLanguage } from "../../context/LanguageContext";


const SurveyPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  
  // Get location data
  const { districts, municipalities, wards, canteenTypes, canteenList, loading } =
    useSelector((state) => state.location);
  
  // Get kiosk survey data from survey reducer
  const { 
    surveyList, 
    surveyDetail, 
    userSurveyList, 
    surveyLoading 
  } = useSelector((state) => state.survey);
  
  // Get kitchen survey data from kitchenSurvey reducer
  const { 
    kitchenSurveyList,
    kitchenSurveyDetail,
    kitchenUserSurveyList,
    loading: kitchenSurveyLoading 
  } = useSelector((state) => state.kitchenSurvey);
  
  // Get kitchenDining survey data from kitchenDiningSurvey reducer
  const { 
    kitchenDiningSurveyList,
    kitchenDiningSurveyDetail,
    kitchenDiningUserSurveyList,
    loading: kitchenDiningSurveyLoading 
  } = useSelector((state) => state.kitchenDiningSurvey);

  const [form, setForm] = useState({
    district: "",
    ulb: "",
    ward: "",
    type: "",
  });
  const [showCanteenList, setShowCanteenList] = useState(false);
  const [selectedCanteen, setSelectedCanteen] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [canteenSurveyStatus, setCanteenSurveyStatus] = useState({});
  const [fetchingSurveys, setFetchingSurveys] = useState(false);
  const [currentSurveyType, setCurrentSurveyType] = useState(null);
  const [completedSurveyMap, setCompletedSurveyMap] = useState({});
  const [canteenSurveyDates, setCanteenSurveyDates] = useState({});
  
  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchCanteenTypes());
  }, [dispatch]);

  // Get today's date in YYYY-MM-DD format
  const getTodayFormatted = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDistrictChange = (e) => {
    const districtId = e.target.value;
    setForm({ district: districtId, ulb: "", ward: "", type: "" });
    setShowCanteenList(false);
    if (districtId) dispatch(fetchMunicipalities(districtId));
  };

  const handleULBChange = (e) => {
    const municipalityId = e.target.value;
    setForm((prev) => ({ ...prev, ulb: municipalityId, ward: "", type: "" }));
    setShowCanteenList(false);
    if (municipalityId) dispatch(fetchWards(municipalityId));
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSearch = async () => {
    setFetchingSurveys(true);
    setCanteenSurveyStatus({});
    setCanteenSurveyDates({});
    setCompletedSurveyMap({});
    setShowCanteenList(false);
    setSelectedCanteen(null);
    setSelectedSurvey(null);
    setCurrentSurveyType(null);
    
    try {
      // Fetch canteen list based on filters
      await dispatch(
        fetchCanteenList({
          district_id: form.district,
          municipality_id: form.ulb,
          ward_id: form.ward,
          canteen_type: form.type,
        })
      );
      
      // Fetch user's survey lists to check completion status
      const type = parseInt(form.type);
      const today = getTodayFormatted();
      
      // Log for debugging
      console.log("Fetching surveys for date:", today);
      
      if (type === 1) { // Kiosk
        await dispatch(fetchUserSurveyList());
      } else if (type === 2) { // Kitchen
        await dispatch(fetchKitchenUserSurveyList());
      } else if (type === 3) { // KitchenDining
        await dispatch(fetchKitchenDiningUserSurveyList());
      }
      
      setShowCanteenList(true);
    } catch (error) {
      console.error("Error searching canteens:", error);
    } finally {
      setFetchingSurveys(false);
    }
  };

  // Updated function to check if a specific canteen has completed survey TODAY
  const checkCanteenSurveyCompletion = useCallback((canteen) => {
    const canteenType = parseInt(canteen.canteen_type);
    const today = getTodayFormatted();
    
    // Log for debugging
    console.log("Checking completion for canteen:", canteen.area_name);
    // console.log("Today's date:", today);
    
    // Try to find matching completed survey based on available data
    if (canteenType === 1 && userSurveyList) {
      console.log("Available Kiosk surveys:", userSurveyList);
      
      const matchingSurvey = userSurveyList.find(survey => {
        const isCompleted = survey.info && 
                           survey.info.is_total_survey_complete === 1;
        const isToday = survey.info && 
                       survey.info.reporting_date === today;
        
        // Check if it's the same canteen
        const isSameCanteen = survey.info && 
                             (survey.info.area_name === canteen.area_name ||
                              survey.info.canteen_id === canteen.id ||
                              (survey.info.district_name === canteen.district_name && 
                               survey.info.municipality_name === canteen.municipality_name &&
                               survey.info.ward_name === canteen.ward_name));
        
        if (isCompleted && isToday && isSameCanteen) {
          console.log("Found matching TODAY survey:", survey.info);
          return true;
        }
        return false;
      });
      
      const result = matchingSurvey ? { 
        completed: true, 
        date: matchingSurvey.info.reporting_date,
        surveyInfo: matchingSurvey.info 
      } : { 
        completed: false, 
        date: null,
        surveyInfo: null 
      };
      
      console.log("Result for", canteen.area_name, ":", result.completed);
      return result;
    } 
    else if (canteenType === 2 && kitchenUserSurveyList) {
      const matchingSurvey = kitchenUserSurveyList.find(survey => {
        return survey.info && 
               survey.info.is_total_survey_complete === 1 &&
               survey.info.reporting_date === today &&
               (survey.info.area_name === canteen.area_name ||
                survey.info.canteen_id === canteen.id);
      });
      return matchingSurvey ? { 
        completed: true, 
        date: matchingSurvey.info.reporting_date,
        surveyInfo: matchingSurvey.info 
      } : { 
        completed: false, 
        date: null,
        surveyInfo: null 
      };
    }
    else if (canteenType === 3 && kitchenDiningUserSurveyList) {
      const matchingSurvey = kitchenDiningUserSurveyList.find(survey => {
        return survey.info && 
               survey.info.is_total_survey_complete === 1 &&
               survey.info.reporting_date === today &&
               (survey.info.area_name === canteen.area_name ||
                survey.info.canteen_id === canteen.id);
      });
      return matchingSurvey ? { 
        completed: true, 
        date: matchingSurvey.info.reporting_date,
        surveyInfo: matchingSurvey.info 
      } : { 
        completed: false, 
        date: null,
        surveyInfo: null 
      };
    }
    
    return { completed: false, date: null, surveyInfo: null };
  }, [userSurveyList, kitchenUserSurveyList, kitchenDiningUserSurveyList]);

  // Check survey completion for all canteens when data loads
  useEffect(() => {
    if (canteenList.length > 0 && showCanteenList) {
      console.log("=== Checking survey completion for canteens ===");
      const statusUpdates = {};
      const dateUpdates = {};
      
      canteenList.forEach(canteen => {
        const { completed, date, surveyInfo } = checkCanteenSurveyCompletion(canteen);
        if (completed) {
          statusUpdates[canteen.id] = true;
          dateUpdates[canteen.id] = date;
          console.log(`Canteen ${canteen.id} (${canteen.area_name}): Survey completed today - ${date}`);
          console.log("Survey info:", surveyInfo);
        } else {
          console.log(`Canteen ${canteen.id} (${canteen.area_name}): No survey today`);
        }
      });
      
      setCanteenSurveyStatus(statusUpdates);
      setCanteenSurveyDates(dateUpdates);
      console.log("Final status updates:", statusUpdates);
      console.log("Final date updates:", dateUpdates);
    }
  }, [canteenList, showCanteenList, checkCanteenSurveyCompletion]);

  const getSurveyType = (canteenType) => {
    const type = parseInt(canteenType);
    
    switch (type) {
      case 1:
        return "kiosk";
      case 2:
        return "kitchen";
      case 3:
        return "kitchendining";
      default:
        console.error("Unknown canteen type:", canteenType);
        return "";
    }
  };

  // Check if a canteen has a completed survey TODAY
  const isSurveyCompleted = useCallback((canteenId) => {
    return canteenSurveyStatus[canteenId] === true;
  }, [canteenSurveyStatus]);

  // Get survey date for a canteen
  const getSurveyDate = useCallback((canteenId) => {
    return canteenSurveyDates[canteenId] || null;
  }, [canteenSurveyDates]);

  const handleViewCanteen = async (canteen) => {
    const surveyType = getSurveyType(canteen.canteen_type);
    
    if (!surveyType) {
      alert("Unknown canteen type");
      return;
    }
    
    setSelectedCanteen(canteen);
    setSelectedSurvey(null);
    setCurrentSurveyType(surveyType);

    // Fetch survey list based on canteen type
    if (surveyType === "kiosk") {
      await dispatch(
        fetchSurveyList({
          canteen_id: canteen.id,
          survey_type: "kiosk",
        })
      );
    } else if (surveyType === "kitchen") {
      // Fetch kitchen surveys for this specific canteen
      await dispatch(
        fetchKitchenSurveyList({
          canteen_id: canteen.id,
        })
      );
    } else if (surveyType === "kitchendining") {
      // Fetch kitchenDining surveys for this specific canteen
      await dispatch(
        fetchKitchenDiningSurveyList({
          canteen_id: canteen.id,
        })
      );
    }
  };

  const handleNewSurvey = (canteen) => {
    /* Commented out original survey completion checks
    // First check if we need to re-check completion
    const { completed, surveyInfo } = checkCanteenSurveyCompletion(canteen);
    
    if (completed) {
      const today = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Show the actual survey date from the data
      const surveyDate = surveyInfo?.reporting_date ? 
        new Date(surveyInfo.reporting_date).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : today;
      
      alert(`A survey has already been completed for this canteen on ${surveyDate}. You cannot start a new survey today.`);
      return;
    }

    // Also check from stored status
    if (isSurveyCompleted(canteen.id)) {
      const today = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      alert(`A survey has already been completed for this canteen today (${today}). You cannot start a new survey.`);
      return;
    }
    */
    
    const surveyType = getSurveyType(canteen.canteen_type);
    
    if (!surveyType) {
      alert(`Invalid canteen type: ${canteen.canteen_type}. Please contact support.`);
      return;
    }
    
    // Clear old survey data from localStorage before starting a new one
    localStorage.removeItem("surveyId");
    localStorage.removeItem("selectedSurvey");
    localStorage.removeItem("surveyData");
    localStorage.removeItem("completedSections");
    
    // Clear all section-specific data stored under "survey_section_*"
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("survey_section_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Save for refresh-safe navigation
    const canteenData = {
      ...canteen,
      type_name: getTypeName(canteen.canteen_type),
      surveyType
    };
    
    localStorage.setItem("selectedCanteen", JSON.stringify(canteenData));
    localStorage.setItem("surveyType", surveyType);
    
    // Navigate to sections page with all necessary data
    navigate("/survey/sections", { 
      state: { 
        canteen: canteenData,
        surveyType,
        newSurvey: true
      }
    });
  };

  const getTypeName = (canteenType) => {
    const type = parseInt(canteenType);
    
    switch (type) {
      case 1:
        return "Kiosk";
      case 2:
        return "Kitchen";
      case 3:
        return "Kitchen Cum Dining";
      default:
        return "Unknown";
    }
  };

  const handleViewSurveyDetail = async (survey) => {
    if (currentSurveyType === "kiosk") {
      await dispatch(fetchSurveyDetail(survey.id));
      setSelectedSurvey(survey);
    } else if (currentSurveyType === "kitchen") {
      await dispatch(fetchKitchenSurveyDetail(survey.id));
      setSelectedSurvey(survey);
    } else if (currentSurveyType === "kitchendining") {
      await dispatch(fetchKitchenDiningSurveyDetail(survey.id));
      setSelectedSurvey(survey);
    }
  };

  const handleBackToSurveyList = () => {
    setSelectedSurvey(null);
  };

  const goBack = () => navigate(-1);

  // Get appropriate survey list based on type
  const getSurveyList = () => {
    if (currentSurveyType === "kiosk") {
      return surveyList || [];
    } else if (currentSurveyType === "kitchen") {
      return kitchenSurveyList || [];
    } else if (currentSurveyType === "kitchendining") {
      return kitchenDiningSurveyList || [];
    }
    return [];
  };

  // Get appropriate survey detail based on type
  const getSurveyDetail = () => {
    if (currentSurveyType === "kiosk") {
      return surveyDetail;
    } else if (currentSurveyType === "kitchen") {
      return kitchenSurveyDetail;
    } else if (currentSurveyType === "kitchendining") {
      return kitchenDiningSurveyDetail;
    }
    return null;
  };

  // Get appropriate loading state
  const getSurveyLoading = () => {
    if (currentSurveyType === "kiosk") {
      return surveyLoading;
    } else if (currentSurveyType === "kitchen") {
      return kitchenSurveyLoading;
    } else if (currentSurveyType === "kitchendining") {
      return kitchenDiningSurveyLoading;
    }
    return false;
  };

  // Get appropriate survey list component
  const getSurveyListComponent = () => {
    if (currentSurveyType === "kiosk") {
      return (
        <KioskSurveyList
          canteen={selectedCanteen}
          surveys={getSurveyList()}
          loading={getSurveyLoading()}
          onViewSurveyDetail={handleViewSurveyDetail}
          onBack={handleBackToSurveyList}
          selectedSurvey={selectedSurvey}
          surveyType="kiosk"
        />
      );
    } else if (currentSurveyType === "kitchen") {
      return (
        <KitchenSurveyList
          canteen={selectedCanteen}
          surveys={getSurveyList()}
          loading={getSurveyLoading()}
          onViewSurveyDetail={handleViewSurveyDetail}
          onBack={handleBackToSurveyList}
          selectedSurvey={selectedSurvey}
        />
      );
    } else if (currentSurveyType === "kitchendining") {
      return (
        <KitchenDiningSurveyList
          canteen={selectedCanteen}
          surveys={getSurveyList()}
          loading={getSurveyLoading()}
          onViewSurveyDetail={handleViewSurveyDetail}
          onBack={handleBackToSurveyList}
          selectedSurvey={selectedSurvey}
        />
      );
    }
    return null;
  };

  // Get appropriate survey detail component
  const getSurveyDetailComponent = () => {
    if (currentSurveyType === "kiosk") {
      return (
        <KioskSurveyDetail
          survey={selectedSurvey}
          detail={getSurveyDetail()}
          loading={getSurveyLoading()}
          onBack={handleBackToSurveyList}
        />
      );
    } else if (currentSurveyType === "kitchen") {
      return (
        <KitchenSurveyDetail
          survey={selectedSurvey}
          detail={getSurveyDetail()}
          loading={getSurveyLoading()}
          onBack={handleBackToSurveyList}
        />
      );
    } else if (currentSurveyType === "kitchendining") {
      return (
        <KitchenDiningSurveyDetail
          survey={selectedSurvey}
          detail={getSurveyDetail()}
          loading={getSurveyLoading()}
          onBack={handleBackToSurveyList}
        />
      );
    }
    return null;
  };

  // Debug: Log survey data
  useEffect(() => {
    console.log("=== DEBUG SURVEY DATA ===");
    console.log("Today's date:", getTodayFormatted());
    console.log("Current date/time:", new Date().toISOString());
    console.log("User Survey List (Kiosk):", userSurveyList);
    console.log("Kitchen Survey List:", kitchenUserSurveyList);
    console.log("KitchenDining Survey List:", kitchenDiningUserSurveyList);
    console.log("Canteen List:", canteenList);
    console.log("Canteen Survey Status:", canteenSurveyStatus);
    console.log("Canteen Survey Dates:", canteenSurveyDates);
  }, [userSurveyList, kitchenUserSurveyList, kitchenDiningUserSurveyList, canteenList, canteenSurveyStatus, canteenSurveyDates]);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl p-6 shadow">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={goBack} 
              className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
            >
              ←
            </button>
            <h1 className="text-2xl font-bold text-center">{t.global.survey_dashboard_title}</h1>
            <div className="w-10" />
          </div>

          {/* Search Filters */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <MapPin className="mr-2" size={20} />
              {t.global.search}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* District */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.global.district}</label>
                <select 
                  value={form.district} 
                  onChange={handleDistrictChange} 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t.global.select_district}</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              {/* ULB */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.global.ulb}</label>
                <select 
                  value={form.ulb} 
                  onChange={handleULBChange} 
                  disabled={!form.district}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">{t.global.select_ulb}</option>
                  {municipalities.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Ward */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.global.ward}</label>
                <select 
                  value={form.ward} 
                  name="ward" 
                  onChange={handleChange} 
                  disabled={!form.ulb}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">{t.global.select_ward}</option>
                  {wards.map((w) => (
                    <option key={w.id} value={w.id}>Ward {w.name}</option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.global.program_type}</label>
                <select 
                  value={form.type} 
                  name="type" 
                  onChange={handleChange} 
                  disabled={!form.ward}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">{t.global.select_program_type}</option>
                  {canteenTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.type_name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              disabled={loading || !form.district || !form.ulb || !form.ward || !form.type}
              className={`w-full mt-6 py-3 rounded-lg font-medium transition ${
                loading || !form.district || !form.ulb || !form.ward || !form.type
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t.global.searching}
                </div>
              ) : (
                t.global.search
              )}
            </button>
          </div>

          {/* Loading indicator for fetching surveys */}
          {fetchingSurveys && (
            <div className="text-center p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Checking survey status...</p>
            </div>
          )}

          {/* Results Section */}
          {showCanteenList && !fetchingSurveys && (
            <div className="mt-8">
              {canteenList.length === 0 ? (
                <div className="text-center p-8 border border-dashed border-gray-300 rounded-lg">
                  <Home className="mx-auto text-gray-400 mb-2" size={48} />
                  <p className="text-gray-500">
                    No canteens found for the selected filters
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {canteenList.map((canteen) => (
                    <CanteenCard
                      key={canteen.id}
                      canteen={canteen}
                      isSurveyComplete={isSurveyCompleted(canteen.id)}
                      surveyDate={getSurveyDate(canteen.id)}
                      onViewCanteen={handleViewCanteen}
                      onNewSurvey={handleNewSurvey}
                      getTypeName={getTypeName}
                      getSurveyType={getSurveyType}
                      showSurveyList={selectedCanteen && selectedCanteen.id === canteen.id}
                      surveyListContent={
                        selectedCanteen && selectedCanteen.id === canteen.id && !selectedSurvey && (
                          getSurveyListComponent()
                        )
                      }
                    />
                  ))}

                  {/* Survey Detail */}
                  {selectedSurvey && getSurveyDetailComponent()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};



export default SurveyPage;

